/*
 * Project: bellcloud
 * File: apps/frontend/src/components/device/connectDevice/ConnectionStatusBar.tsx
 * Description: Connection status bar showing badge, serial number, and firmware version
 * Uses global connection store - no props needed
 */

import React from 'react';
import styled from '@emotion/styled';
import { useConnectionStore } from '../../../store/connectionStore';
import { ConnectionBadge } from './ConnectionBadge';
import { Text } from '@/components/ui/common/Typography';
import { parseEquippedSensor } from '@packages/types/device/mini/information';
import { MINI_SENSOR_TYPE_MAP } from '@packages/types/device/mini/sensors';
interface ConnectionStatusBarProps {
  /** Additional action button (e.g., "Import Log") - optional override */
  actionButton?: React.ReactNode;
  /** Show detailed device and cloud information below the status bar */
  showDetailInfo?: boolean;
}

/** Aligns detail block with serial number row; badge width differs by transport (Bluetooth vs Cradle). */
const DETAIL_INFO_LEFT_INSET_PX = {
  ble: 96,
  serial: 80,
} as const;

/**
 * ConnectionStatusBar Component
 *
 * Displays connection status in a horizontal bar:
 * - Left: Connection badge + serial number (or status message)
 * - Right: Firmware version or action button
 * - Optional: Detailed device and cloud information below
 * - Automatically reads from useConnectionStore
 */
export const ConnectionStatusBar: React.FC<ConnectionStatusBarProps> = ({
  actionButton,
  showDetailInfo = false,
}) => {
  const { protocol, device } = useConnectionStore();

  const isConnected = protocol.type !== 'none';
  const serialNumber = device.type !== 'none' ? device.activeDeviceId : null;
  const fwVersion = device.type !== 'none' ? device.fwVersion : undefined;
  const deviceInfo = device.type !== 'none' ? device.info : null;
  const deviceConfig = device.type !== 'none' ? device.config : null;

  let isOverdue = false;
  /**
   * Render left section (badge + serial number or status)
   */
  const renderLeftSection = () => {
    // No device connected
    if (protocol.type === 'none') {
      return (
        <>
          <Dash>-</Dash>
          <StatusText $isSecondary>No Device Connected</StatusText>
        </>
      );
    }

    // Device connected and loaded
    return (
      <>
        <ConnectionBadge />
        <SerialNumber>{serialNumber || '-'}</SerialNumber>
      </>
    );
  };

  /**
   * Render right section (firmware version or action button)
   */
  const renderRightSection = () => {
    if (actionButton) {
      return actionButton;
    }

    if (isConnected && device.type !== 'none' && fwVersion) {
      return <FirmwareVersion>FW {fwVersion}</FirmwareVersion>;
    }

    return null;
  };

  // Extract device type from store
  const deviceType =
    device.type !== 'none' ? (device.type === 'bellmini' ? 'BELLMINI' : 'BELLLITE') : null;

  // BellLite: sensor_type is an array of objects with label e.g. [{label:'CO'}]
  // BellMini: equipped_sensor is a number enum e.g. 19 (CO2)
  const getSensorTypeDisplay = () => {
    const info = deviceInfo as any;

    if (device.type === 'bellmini') {
      const equippedSensor = info?.equipped_sensor;
      if (equippedSensor !== undefined && equippedSensor !== null) {
        return parseEquippedSensor(equippedSensor);
      }
      const sensorName = info?.sensor_type?.[0];
      if (sensorName) {
        const mapped = MINI_SENSOR_TYPE_MAP[String(sensorName)];
        return mapped !== undefined ? parseEquippedSensor(mapped) : String(sensorName);
      }
      return '-';
    }

    if (device.type === 'belllite') {
      const sensorTypes: Array<{ value: number; label: string }> = info?.sensor_type ?? [];
      const labels = sensorTypes.filter(s => s.label && s.value !== 255).map(s => s.label);
      return labels.length > 0 ? labels.join(', ') : '-';
    }

    return '-';
  };

  // Calibration due display
  // Both Mini and Lite store calibration due as days remaining (positive = remaining, negative = overdue)
  // Mini: calibration_due_date (number)
  // Lite: min_calibration_due_date (number, minimum across all sensors)
  //
  // If every sensor's calibration_interval in DEVICE CONFIGURATION is 0,
  // the device is not tracking calibration due dates → display '-'.
  const getCalibrationDueDisplay = (): string => {
    if (device.type === 'none') return '-';

    // Calibration not managed when all intervals are 0 (or array is missing/empty)
    const intervals = (deviceConfig as any)?.calibration_interval as number[] | undefined;
    const isCalibrationUnmanaged =
      Array.isArray(intervals) && intervals.length > 0 && intervals.every(v => v === 0);
    if (isCalibrationUnmanaged) {
      isOverdue = false;
      return '-';
    }

    const info = deviceInfo as any;
    let dueDays: number | undefined;
    if (device.type === 'bellmini') {
      dueDays = info?.calibration_due_date;
    } else {
      dueDays = info?.min_calibration_due_date;
    }

    if (dueDays === undefined || dueDays === null) return '-';
    if (typeof dueDays !== 'number') return String(dueDays);

    if (dueDays < 0) {
      isOverdue = true;
      return `${Math.abs(dueDays)} days ago`;
    }
    if (dueDays === 0) {
      isOverdue = false;
      return 'Today';
    }
    isOverdue = false;
    return `${dueDays} days left`;
  };

  const sensorType = getSensorTypeDisplay();
  const calibrationDue = getCalibrationDueDisplay();

  return (
    <Wrapper>
      <StatusBarContainer $isConnected={isConnected}>
        <LeftSection>{renderLeftSection()}</LeftSection>
        <RightSection>{renderRightSection()}</RightSection>
      </StatusBarContainer>

      {/* Detailed Device and Cloud Information */}
      {showDetailInfo && isConnected && device.type !== 'none' && deviceInfo && (
        <DetailInfoContainer
          $leftPaddingPx={
            protocol.type === 'serial'
              ? DETAIL_INFO_LEFT_INSET_PX.serial
              : DETAIL_INFO_LEFT_INSET_PX.ble
          }
        >
          <Section>
            <Text size="md" weight="medium" color="textPrimary">
              Device Information
            </Text>
            <InfoGrid>
              <InfoRow>
                <Text size="sm" weight="medium" color="textSecondary">
                  Device Type
                </Text>
                <Text size="sm" weight="semibold" color="textPrimary">
                  {deviceType}
                </Text>
              </InfoRow>

              <InfoRow>
                <Text size="sm" weight="medium" color="textSecondary">
                  Sensor
                </Text>
                <Text size="sm" weight="semibold" color="textPrimary">
                  {sensorType}
                </Text>
              </InfoRow>

              <InfoRow>
                <Text size="sm" weight="medium" color="textSecondary">
                  Calibration Due
                </Text>
                {calibrationDue === '-' ? (
                  <Text size="sm" weight="semibold" color="textPrimary">
                    -
                  </Text>
                ) : (
                  <CalibrationBadge $isOverdue={isOverdue}>{calibrationDue}</CalibrationBadge>
                )}
              </InfoRow>
            </InfoGrid>
          </Section>
        </DetailInfoContainer>
      )}
    </Wrapper>
  );
};

// ==================== Styled Components ====================

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  flex: 1;
  min-width: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
`;

const Dash = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.disabled};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const StatusText = styled.span<{ $isSecondary?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.md}px;
  color: ${({ theme, $isSecondary }) =>
    $isSecondary ? theme.colors.text.secondary : theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const SerialNumber = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: 'Inter', monospace;
  letter-spacing: 0.5px;
`;

const FirmwareVersion = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatusBarContainer = styled.div<{ $isConnected: boolean }>`
  width: 100%;
  height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[5]}px;
  background-color: ${({ theme, $isConnected }) =>
    $isConnected ? theme.colors.surface.group?.default : theme.colors.surface.interactive.hover};
  border-radius: ${({ theme }) => theme.borderRadius.md}px ${({ theme }) => theme.borderRadius.md}px
    0 0;
  gap: ${({ theme }) => theme.spacing[4]}px;
`;
// Detail Info Styled Components
const DetailInfoContainer = styled.div<{ $leftPaddingPx: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 ${({ theme }) => theme.spacing[5]}px ${({ theme }) => theme.spacing[5]}px
    ${({ $leftPaddingPx }) => $leftPaddingPx}px;
  background-color: ${({ theme }) => theme.colors.surface.group?.default};
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.md}px
    ${({ theme }) => theme.borderRadius.md}px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoRow = styled.div`
  min-height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CalibrationBadge = styled.span<{ $isOverdue: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[1]}px ${theme.spacing[3]}px`};
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 1px solid
    ${({ theme, $isOverdue }) =>
      $isOverdue
        ? theme.colors.status.danger.stroke.border
        : theme.colors.status.stable.stroke.border};
  color: ${({ theme, $isOverdue }) =>
    $isOverdue ? theme.colors.status.danger.stroke.text : theme.colors.status.stable.stroke.text};
`;
